import { useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { NotificationCategory } from '@pelicargo/types';
import { shipmentStatusOptions, shipmentStatusUpdateMessages } from '@pelicargo/utils';
import { ShipmentStatus } from '@prisma/client';
import { uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useEmail } from '../../hooks/useEmail';
import {
  getCustomerDefaultsFromRequest,
  getSharedAirlineDefaultsFromRequest,
  hasCustomerEmail,
  recipientValidationSchema,
  SharedEmailValidationSchema,
} from '../../utils/email';
import { EmailDrawer } from '../Emails/EmailDrawer';
import { EmailDrawerLeftPanel } from '../Emails/EmailDrawerLeftPanel';
import { EmailDrawerRightPanel } from '../Emails/EmailDrawerRightPanel';
import { EmailQuoteForm } from '../Emails/EmailQuoteForm';
import { useRequestDrawersContext } from '../RequestDrawers';
import { SimpleCustomerRecipientForm } from '../SimpleCustomerRecipientForm';
import { SimpleForm } from '../SimpleForm';
import { SimpleSelect } from '../SimpleSelect';

const validationSchema = z.object({
  ...SharedEmailValidationSchema,
  isLongEmail: z.boolean(),
  recipients: z.array(recipientValidationSchema).min(1),
  shipmentStatus: z.nativeEnum(ShipmentStatus).nullable(),
});

export const StatusUpdateEmailDrawer = () => {
  const toast = useToast();
  const { request } = useCurrentRequest();
  const { data: contacts } = trpc.getUsersContacts.useQuery(
    { ids: [request?.default_sender_contact?.user?.id] },
    { enabled: !!request?.default_sender_contact?.user?.id },
  );

  const { data: defaultContacts } = trpc.getDefaultContactsByRequest.useQuery(
    { id: request?.id, updates_for: NotificationCategory.Quote },
    { enabled: !!request?.id },
  );

  const { getIsModalOpen, closeModal, getModalData } = useRequestDrawersContext();
  const isOpen = getIsModalOpen('StatusUpdate');
  const onClose = () => closeModal('StatusUpdate');
  const { name, subject } = getModalData('StatusUpdate');

  const { user } = useAuth();
  const defaultValues = useMemo(() => {
    return {
      ...getSharedAirlineDefaultsFromRequest(request),
      ...getCustomerDefaultsFromRequest(request),
      isLongEmail: false,
      senderContactId: user?.contact_id,
      customMessage: shipmentStatusUpdateMessages(request, request.booked_quote_variant.quote),
      shipmentStatus: request?.shipment_status,
    } satisfies z.infer<typeof validationSchema>;
  }, [request, user?.contact_id]);

  const methods = useForm<Partial<z.infer<typeof validationSchema>>>({
    mode: 'onSubmit',
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const status = useWatch({
    control: methods.control,
    name: 'shipmentStatus',
  });
  const recipients = methods.watch('recipients');
  const defaultSelectedContacts = uniqBy([...(contacts || []), ...(defaultContacts || [])].filter(Boolean), 'id');

  const { sendGenericEmailToFF, isLoading } = useEmail();
  const { mutateAsync: updateRequest } = trpc.updateRequest.useMutation();

  const handleSendEmail = async (overrides) => {
    try {
      const requestId = request?.id;
      if (!requestId) return;

      const { senderContactId } = overrides;

      const toContact = recipients?.[0];

      const ccContacts = recipients?.slice(1);

      await sendGenericEmailToFF({
        requestId,
        senderContactId,
        toContact,
        ccContacts,
        overrides: {
          ...overrides,
          subjectEnd: subject,
        },
      });
      await updateRequest({
        id: request?.id,
        shipment_status: status,
      });

      toast({
        title: 'Success',
        description: overrides?.clipboard ? 'Email copied to clipboard' : 'Email sent successfully',
        status: 'success',
      });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  const canSend = !!recipients?.length;

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const customMessage = methods.watch('customMessage');
  const defaultRequest = {
    ...request,
    customMessage,
  };

  const showWarning = useMemo(() => {
    if (!recipients?.length) return false;

    return hasCustomerEmail(recipients);
  }, [recipients]);

  const changeStatus = useCallback(
    (e) => {
      methods.setValue(
        'customMessage',
        shipmentStatusUpdateMessages(
          { id: request?.id, shipment_status: e.target.value },
          request.booked_quote_variant.quote,
        ),
      );
    },
    [methods, request],
  );

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <EmailDrawer isOpen={isOpen} onClose={handleClose}>
        <EmailDrawerLeftPanel
          title="Specify contact(s)"
          description="Specify the contacts you want to send the email to"
          w="50%"
        >
          <SimpleCustomerRecipientForm
            name="recipients"
            defaultAccount={request?.account}
            defaultSelectedContacts={defaultSelectedContacts}
            searchPattern="FF"
          />
        </EmailDrawerLeftPanel>
        <EmailDrawerRightPanel title={name} w="50%">
          <SimpleSelect
            options={shipmentStatusOptions}
            label="Shipment Status"
            name="shipmentStatus"
            onChange={changeStatus}
          />
          <EmailQuoteForm
            request={defaultRequest}
            onSubmit={handleSendEmail}
            onClose={onClose}
            isLoading={isLoading}
            canSend={canSend}
            hasCustomerEmail={showWarning}
          />
        </EmailDrawerRightPanel>
      </EmailDrawer>
    </SimpleForm>
  );
};
